import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { workflowsApi } from '@/lib/api/workflows.api';

import type { CreateWorkflowEntityDto, UpdateWorkflowEntityDto } from '@repo/shared-types';

const QUERY_KEY = ['workflows'];

export const useWorkflows = (options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: QUERY_KEY,
    queryFn: workflowsApi.getAll,
    enabled: options?.enabled ?? true,
  });

export const useWorkflow = (id: string | undefined) =>
  useQuery({
    queryKey: [...QUERY_KEY, id],
    queryFn: () => workflowsApi.getOne(id!),
    enabled: !!id,
  });

export const useCreateWorkflow = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateWorkflowEntityDto) => workflowsApi.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

export const useUpdateWorkflow = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateWorkflowEntityDto }) =>
      workflowsApi.update(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

export const useDeleteWorkflow = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => workflowsApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

export const useExecuteWorkflow = () => {
  return useMutation({
    mutationFn: ({
      callId,
      workflowId,
      dryRun,
    }: {
      callId: string;
      workflowId?: string;
      dryRun?: boolean;
    }) => workflowsApi.executeForCall(callId, { workflowId, dryRun }),
  });
};

export const useRunWorkflow = () => {
  return useMutation({
    mutationFn: (id: string) => workflowsApi.runWorkflow(id),
  });
};

export const useGenerateWebhookToken = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => workflowsApi.generateWebhookToken(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

export const useWebhookTestPayload = (id: string | undefined) =>
  useQuery({
    queryKey: [...QUERY_KEY, id, 'webhook-test'],
    queryFn: () => workflowsApi.getWebhookTestPayload(id!),
    enabled: !!id,
    refetchInterval: false,
  });

export const useWorkflowExecutionLogs = (params: {
  page?: number;
  limit?: number;
  workflowId?: string;
}) =>
  useQuery({
    queryKey: [...QUERY_KEY, 'execution-logs', params],
    queryFn: () => workflowsApi.getExecutionLogs(params),
  });

export const useWorkflowExecutionLog = (id: string | undefined) =>
  useQuery({
    queryKey: [...QUERY_KEY, 'execution-logs', id],
    queryFn: () => workflowsApi.getExecutionLog(id!),
    enabled: !!id,
  });
